import SeoHead from '../components/SeoHead';
import ContactForm from '../components/ContactForm';
import { COMPANY } from '../data/seedData';

export default function ContactPage() {
  return (
    <>
      <SeoHead
        title="Kontakt - RB Glasfiber A/S"
        description="Kontakt RB Glasfiber A/S i Mønsted, Viborg. Telefon: 30 13 61 16, e-mail: torben@rb-glasfiber.dk. Udkørende glasfiberreparation og produktion."
        canonical="https://www.rb-glasfiber.dk/kontakt"
      />

      {/* Hero Header */}
      <section className="relative pt-32 pb-16 lg:pt-40 lg:pb-24 bg-slate-950 text-white overflow-hidden border-b border-slate-800">
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded bg-[#1e9457]/20 border border-[#1e9457]/40 text-[#22c55e] text-xs font-bold uppercase tracking-widest mb-4">
              <i className="fa-solid fa-headset"></i>
              <span>Kontakt & Rådgivning</span>
            </div>
            <h1 className="text-3xl sm:text-5xl font-extrabold uppercase tracking-tight text-white mb-6">
              Kontakt RB Glasfiber
            </h1>
            <p className="text-base sm:text-lg text-slate-300 leading-relaxed max-w-2xl">
              Uanset om du søger rådgivning om nye brønde og tanke, standardcontainere eller har akut brug for udkørende reparation i glasfiber, er vi altid klar til at hjælpe.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            {/* Left Col: Contact Information */}
            <div className="lg:col-span-5 space-y-8">
              <div>
                <div className="inline-flex items-center gap-2 text-xs font-bold text-[#1e9457] uppercase tracking-wider">
                  <span className="w-2 h-2 rounded-full bg-[#1e9457]"></span>
                  Direkte kontakt
                </div>
                <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 uppercase mt-1">
                  Vi tror på den gode dialog
                </h2>
                <p className="text-slate-600 text-sm mt-2 leading-relaxed">
                  Ring direkte til os eller send en besked. Vi besvarer henvendelser hurtigt og fagligt.
                </p>
              </div>

              {/* Direct Details Cards */}
              <div className="space-y-4">
                <a
                  href={`tel:${COMPANY.phoneRaw}`}
                  className="p-5 rounded-xl border border-slate-200 bg-slate-50 hover:bg-white hover:border-[#1e9457] hover:shadow-md transition-all flex items-start gap-4 group"
                >
                  <div className="w-12 h-12 rounded-lg bg-[#1e9457] text-white flex items-center justify-center flex-shrink-0 text-xl group-hover:scale-105 transition-transform">
                    <i className="fa-solid fa-phone"></i>
                  </div>
                  <div>
                    <div className="text-xs uppercase font-bold tracking-wider text-slate-500">Telefon</div>
                    <div className="text-lg font-bold text-slate-900 group-hover:text-[#1e9457] transition-colors whitespace-nowrap">
                      {COMPANY.phone}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">Ring for hurtig afklaring eller akut reparation</div>
                  </div>
                </a>

                <a
                  href={`mailto:${COMPANY.email}`}
                  className="p-5 rounded-xl border border-slate-200 bg-slate-50 hover:bg-white hover:border-[#1e9457] hover:shadow-md transition-all flex items-start gap-4 group"
                >
                  <div className="w-12 h-12 rounded-lg bg-slate-800 text-[#1e9457] flex items-center justify-center flex-shrink-0 text-xl group-hover:scale-105 transition-transform">
                    <i className="fa-solid fa-envelope"></i>
                  </div>
                  <div>
                    <div className="text-xs uppercase font-bold tracking-wider text-slate-500">E-mail</div>
                    <div className="text-base sm:text-lg font-bold text-slate-900 group-hover:text-[#1e9457] transition-colors whitespace-nowrap">
                      {COMPANY.email}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">Send forespørgsel, tegninger eller specifikationer</div>
                  </div>
                </a>

                <div className="p-5 rounded-xl border border-slate-200 bg-slate-50 flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-slate-800 text-slate-300 flex items-center justify-center flex-shrink-0 text-xl">
                    <i className="fa-solid fa-location-dot"></i>
                  </div>
                  <div>
                    <div className="text-xs uppercase font-bold tracking-wider text-slate-500">Adresse & Værksted</div>
                    <div className="text-sm font-bold text-slate-900">RB Glasfiber A/S</div>
                    <div className="text-sm text-slate-600">Jernvej 6, Mønsted</div>
                    <div className="text-sm text-slate-600">8800 Viborg</div>
                  </div>
                </div>

                <div className="p-5 rounded-xl border border-slate-200 bg-slate-50 flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-slate-800 text-slate-300 flex items-center justify-center flex-shrink-0 text-xl">
                    <i className="fa-solid fa-user-tie"></i>
                  </div>
                  <div>
                    <div className="text-xs uppercase font-bold tracking-wider text-slate-500">Daglig ledelse</div>
                    <div className="text-sm font-bold text-slate-900">{COMPANY.contactPerson}</div>
                    <div className="text-xs text-slate-500 mt-0.5">CVR: <span className="font-mono text-slate-700">{COMPANY.cvr}</span></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Col: Contact Form */}
            <div className="lg:col-span-7">
              <ContactForm />
            </div>
          </div>

          {/* Location Map */}
          <div className="space-y-4 pt-8 border-t border-slate-200">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold text-slate-900 uppercase">Find os på kortet</h3>
                <p className="text-slate-600 text-xs sm:text-sm">Jernvej 6, Mønsted, 8800 Viborg</p>
              </div>
              <a
                href="https://maps.google.com/?cid=9654608359521884386"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-xs font-bold text-[#1e9457] hover:underline"
              >
                <span>Åbn i Google Maps</span>
                <i className="fa-solid fa-arrow-up-right-from-square text-[10px]"></i>
              </a>
            </div>

            <div className="rounded-xl overflow-hidden border border-slate-300 shadow-sm aspect-[16/7] w-full bg-slate-100">
              <iframe
                title="RB Glasfiber A/S Beliggenhed"
                src="https://maps.google.com/maps?q=Jernvej+6,+8800+Viborg,+Danmark&t=&z=14&ie=UTF8&iwloc=&output=embed"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
